import type { ReactNode } from 'react';
import { money as formatMoney } from './format.js';
import { Note, Panel, TableWrap } from './components.js';

/**
 * A generated report, on screen.
 *
 * The same document the API renders as a printable page and as a spreadsheet,
 * drawn from the same structure by the same rules. That is the whole reason
 * this is one component fed by the server's own model rather than a table built
 * by each panel: a preview that quietly disagrees with the file is worse than
 * no preview, because it is the preview somebody checks and the file somebody
 * sends.
 *
 * What it does not promise is that the file is the same *moment*. Downloading
 * runs the report again against live records, so a refund settled in between
 * will be in the file and not in the preview. Every document carries the time
 * it was produced on its face, and says so in its own notes, because two copies
 * of one period that differ by a refund is a normal thing that has to be
 * explainable rather than a discrepancy somebody has to discover.
 *
 * It carries no language of its own. Every heading, label and note comes from
 * the document, so the merchant panel renders it in English and the admin
 * console renders it in Bulgarian through exactly this code.
 */

export type ReportColumnType = 'text' | 'money' | 'integer' | 'date' | 'datetime';

export interface ReportColumn {
  key: string;
  header: string;
  type: ReportColumnType;
  currencyKey?: string;
  width?: number;
  printOnly?: boolean;
}

export interface ReportTotalLine {
  label: string;
  values: Record<string, unknown>;
}

export interface ReportTable {
  key: string;
  title: string;
  note?: string;
  columns: ReportColumn[];
  rows: Array<Record<string, unknown>>;
  totals?: ReportTotalLine[];
  empty: string;
}

export interface ReportNotice {
  tone: 'info' | 'warn' | 'danger';
  title: string;
  body: string;
}

export interface ReportWords {
  preparedFor: string;
  preparedBy: string;
  period: string;
  generated: string;
  page: string;
  noRows: string;
  printHint: string;
  printButton: string;
  notes: string;
  reference: string;
}

export interface ReportDocument {
  slug: string;
  title: string;
  subtitle?: string;
  preparedFor: Array<[string, string]>;
  preparedBy: Array<[string, string]>;
  period: { from: string; to: string; label: string };
  generatedAt: string;
  locale: string;
  /**
   * How money is written, where the platform wants a different answer from the
   * one the rest of the document uses. The admin console keeps its amounts in
   * Stripe's own rendering while its words and dates are Bulgarian - see
   * apps/admin/src/format.ts. Unset means money follows `locale`.
   */
  moneyLocale?: string;
  notices: ReportNotice[];
  tables: ReportTable[];
  footnotes: string[];
  words: ReportWords;
}

// ---------------------------------------------------------------------------

/**
 * UTC, deliberately, and the same choice the server makes.
 *
 * A period boundary that moves with the reader's clock cannot be reproduced,
 * and a preview that puts a payment on the tenth while the exported file puts
 * it on the ninth is exactly the sort of discrepancy that costs an afternoon.
 * Every document says on its face that its times are UTC.
 */
function formatDay(value: string, locale: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

function formatMoment(value: string, locale: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'UTC',
    }).format(date);
  } catch {
    return date.toISOString().replace('T', ' ').slice(0, 16);
  }
}

/** One cell, in the document's own conventions. Blank when there is nothing. */
export function reportCell(
  row: Record<string, unknown>,
  column: ReportColumn,
  locale: string,
  moneyLocale: string = locale,
): string {
  const raw = row[column.key];
  if (raw === null || raw === undefined || raw === '') return '';

  if (column.type === 'money') {
    const currency = column.currencyKey ? row[column.currencyKey] : null;
    if (typeof raw !== 'number' || typeof currency !== 'string' || !currency) return '';
    return formatMoney(raw, currency, moneyLocale);
  }
  if (column.type === 'integer') {
    if (typeof raw !== 'number') return '';
    try {
      return new Intl.NumberFormat(locale).format(raw);
    } catch {
      return String(raw);
    }
  }
  if (column.type === 'date') return formatDay(String(raw), locale);
  if (column.type === 'datetime') return formatMoment(String(raw), locale);
  return String(raw);
}

function isNumeric(column: ReportColumn): boolean {
  return column.type === 'money' || column.type === 'integer';
}

// ---------------------------------------------------------------------------

function Parties({ heading, entries }: { heading: string; entries: Array<[string, string]> }) {
  if (entries.length === 0) return null;
  return (
    <div style={{ borderLeft: '2px solid var(--wc-border-strong)', paddingLeft: 12 }}>
      <h3
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'var(--wc-muted)',
        }}
      >
        {heading}
      </h3>
      <dl
        style={{
          margin: '5px 0 0',
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: '2px 10px',
          fontSize: 13,
        }}
      >
        {entries.map(([label, value]) => (
          <ReportPair key={`${label}:${value}`} label={label} value={value} />
        ))}
      </dl>
    </div>
  );
}

function ReportPair({ label, value }: { label: string; value: string }) {
  // Identifiers are monospaced so two of them can be compared by eye, which is
  // what somebody does when checking a report against the console.
  const identifier = /^[a-z]+_[A-Za-z0-9]{6,}$/.test(value);
  return (
    <>
      <dt style={{ color: 'var(--wc-muted)' }}>{label}</dt>
      <dd
        style={{ margin: 0, color: 'var(--wc-ink)' }}
        className={identifier ? 'wc-num' : undefined}
      >
        {value}
      </dd>
    </>
  );
}

function ReportTableView({
  table,
  locale,
  moneyLocale,
  actions,
}: {
  table: ReportTable;
  locale: string;
  moneyLocale: string;
  actions?: ReactNode;
}) {
  const totals = table.totals ?? [];

  return (
    <Panel title={table.title} actions={actions} padded={false}>
      {table.note ? (
        <p
          style={{
            margin: 0,
            padding: '10px 16px',
            borderBottom: '1px solid var(--wc-border)',
            color: 'var(--wc-muted)',
            fontSize: 12.5,
            maxWidth: '82ch',
          }}
        >
          {table.note}
        </p>
      ) : null}

      {table.rows.length === 0 && totals.length === 0 ? (
        <div
          style={{
            padding: '34px 16px',
            textAlign: 'center',
            color: 'var(--wc-faint)',
            fontSize: 13.5,
          }}
        >
          {table.empty}
        </div>
      ) : (
        <TableWrap>
          <table className="wc-table">
            <thead>
              <tr>
                {table.columns.map((column) => (
                  <th key={column.key} className={isNumeric(column) ? 'wc-right' : undefined}>
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, i) => (
                <tr key={`${table.key}-${i}`}>
                  {table.columns.map((column) => (
                    <td
                      key={column.key}
                      className={isNumeric(column) ? 'wc-right wc-num' : undefined}
                    >
                      {reportCell(row, column, locale, moneyLocale)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            {totals.length > 0 ? (
              <tfoot>
                {totals.map((total, i) => (
                  <tr key={`${table.key}-total-${i}`}>
                    {table.columns.map((column, index) => (
                      <td
                        key={column.key}
                        className={isNumeric(column) ? 'wc-right wc-num' : undefined}
                        style={{
                          fontWeight: 600,
                          color: 'var(--wc-ink)',
                          borderTop: i === 0 ? '2px solid var(--wc-border-strong)' : undefined,
                          background: 'var(--wc-surface-alt)',
                        }}
                      >
                        {index === 0
                          ? total.label
                          : reportCell(total.values, column, locale, moneyLocale)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tfoot>
            ) : null}
          </table>
        </TableWrap>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------

export function ReportView({
  document,
  actions,
  tableActions,
}: {
  document: ReportDocument;
  /** Print and download controls, rendered beside the document heading. */
  actions?: ReactNode;
  /** A control for one table, e.g. exporting just that table as CSV. */
  tableActions?: (table: ReportTable) => ReactNode;
}) {
  return (
    /*
     * `minmax(0, 1fr)` rather than the implicit `auto` track.
     *
     * A grid item is `min-width: auto` by default, so a table wider than the
     * page widens the track, the track widens the page, and the whole document
     * scrolls sideways instead of the table scrolling inside its own box. This
     * is the one line that keeps a fourteen-column transaction listing from
     * pushing the panel chrome off the screen.
     */
    <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'minmax(0, 1fr)' }}>
      <section className="wc-panel">
        <div style={{ padding: '18px 20px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: 20,
              flexWrap: 'wrap',
              paddingBottom: 12,
              borderBottom: '2px solid var(--wc-blue-ink)',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                className="wc-num"
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--wc-blue-ink)',
                }}
              >
                {document.preparedBy[0]?.[1] ?? 'WyoCSPay'}
              </div>
              <h2 style={{ marginTop: 3, fontSize: 21 }}>{document.title}</h2>
              {document.subtitle ? (
                <p style={{ margin: '2px 0 0', color: 'var(--wc-muted)', fontSize: 13.5 }}>
                  {document.subtitle}
                </p>
              ) : null}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {actions}
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
              gap: 18,
              marginTop: 16,
            }}
          >
            <Parties heading={document.words.preparedFor} entries={document.preparedFor} />
            <Parties heading={document.words.preparedBy} entries={document.preparedBy} />
            <Parties
              heading={document.words.period}
              entries={[
                [document.words.period, document.period.label],
                [document.words.generated, `${formatMoment(document.generatedAt, document.locale)} UTC`],
              ]}
            />
          </div>
        </div>
      </section>

      {document.notices.map((notice, i) => (
        <Note
          key={`${notice.title}-${i}`}
          tone={notice.tone === 'warn' ? 'warn' : notice.tone === 'danger' ? 'danger' : 'info'}
          title={notice.title}
        >
          {notice.body}
        </Note>
      ))}

      {document.tables.map((table) => (
        <ReportTableView
          key={table.key}
          table={table}
          locale={document.locale}
          moneyLocale={document.moneyLocale ?? document.locale}
          actions={tableActions?.(table)}
        />
      ))}

      {document.footnotes.length > 0 ? (
        <Panel title={document.words.notes}>
          <ol
            style={{
              margin: 0,
              paddingLeft: 18,
              color: 'var(--wc-muted)',
              fontSize: 12.5,
              display: 'grid',
              gap: 4,
            }}
          >
            {document.footnotes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ol>
        </Panel>
      ) : null}
    </div>
  );
}
